import React, { Component } from "react";
import { Modal } from "react-bootstrap";
import MultiSelect from "react-multi-select-component";
import { PiBriefcase, PiBuildings, PiMapPin } from "react-icons/pi";
import EvsDetailTabs from "./evs-detail-tabs";
import EvsDetailScoreAnalysis from "./evs-detail-score-analysis";
import EvsDetailCampaigns from "./evs-detail-campaigns";
import EvsAttackVectorGauge from "./evs-attack-vector-gauge";
import axios from '../../../axios';
import apiError from '../../../error';
import "./evs-detail-drawer.css";

const orDash = (v) => (v != null && String(v).trim() !== "" ? String(v).trim() : "-");

	export default class EvsDetailDrawer extends Component {
		_isMounted = false;
	state = {
		activeTab: "score-analysis",
		attackTypes: [],
		selectedAttackOptions: [],
		averageEvsScore: 0,
	};


	handleTabChange = (tabId) => this.setState({ activeTab: tabId });

		handleAverageEvsChange = (value) => {
			const safeValue = Math.max(0, Math.min(100, Number(value) || 0));
			this.setState({ averageEvsScore: safeValue });
		};

		getTargetId = (evs) => evs?.target?.id ?? evs?.targetId ?? null;

		attackTypeIdsKey = (selectedAttackOptions) =>
			Array.isArray(selectedAttackOptions) && selectedAttackOptions.length
				? selectedAttackOptions.map((opt) => String(opt?.value)).sort().join(",")
				: "";

		fetchAverageEvsForCampaignTab = () => {
			const targetId = this.getTargetId(this.props.evs);
			if (!targetId) return;
			const attackTypeIds = (this.state.selectedAttackOptions || [])
				.map((opt) => opt?.value)
				.filter((id) => id != null && id !== "");
			const params = { graphType: "evs-score" };
			if (attackTypeIds.length) params.attackTypeIds = attackTypeIds;

			axios
				.get(`/api/evs/analytics/${targetId}`, { params })
				.then((res) => {
					if (!this._isMounted) return;
					const root =
						res?.data?.data && typeof res.data.data === "object"
							? res.data.data
							: res?.data || {};
					const payload =
						root && Object.prototype.hasOwnProperty.call(root, "evsScore")
							? root.evsScore
							: root;
					const avgRaw = payload?.averageEvsPercentage;
					const avg = Math.max(0, Math.min(100, Number(avgRaw) || 0));
					this.handleAverageEvsChange(avg);
				})
				.catch((err) => apiError(err));
		};

		handleClose = () => {
			// Reset drawer-specific UI state when it closes
			this.setState({
				activeTab: "score-analysis",
				selectedAttackOptions: [],
				averageEvsScore: 0,
			});
			if (this.props.onHide) {
				this.props.onHide();
			}
		};

	handleExport = () => {
		if (this.props.onExport) this.props.onExport(this.props.evs);
	};

		loadAttackType = () => {
			axios
				.get("/api/attacktypes", {})
				.then((res) => {
					if (!this._isMounted) return;
					this.setState({ attackTypes: res.data.attackTypes });
				})
				.catch((err) => apiError(err));
		};
	
		componentDidMount = () => {
			this._isMounted = true;
			this.loadAttackType();
		};

		componentWillUnmount = () => {
			this._isMounted = false;
		};

		componentDidUpdate(prevProps, prevState) {
			const prevTargetId = this.getTargetId(prevProps.evs);
			const nextTargetId = this.getTargetId(this.props.evs);
			const targetChanged = prevTargetId !== nextTargetId && nextTargetId;
			const tabChangedToCampaigns =
				prevState.activeTab !== "campaigns" && this.state.activeTab === "campaigns";
			const attackChangedOnCampaigns =
				this.state.activeTab === "campaigns" &&
				this.attackTypeIdsKey(prevState.selectedAttackOptions) !==
					this.attackTypeIdsKey(this.state.selectedAttackOptions);

			if (tabChangedToCampaigns || attackChangedOnCampaigns || targetChanged) {
				this.fetchAverageEvsForCampaignTab();
			}
		}

	getSeverityInfo = (value) => {
		const v = Math.max(0, Math.min(100, Number(value) || 0));

		// Match gauge chart segments:
		// 0–25  : Low
		// 25–50 : Medium
		// 50–75 : High
		// 75–100: Critical
		if (v < 25) {
			return { label: "Low", className: "evs-detail-drawer__attack-vector-severity--low" };
		}
		if (v < 50) {
			return { label: "Medium", className: "evs-detail-drawer__attack-vector-severity--medium" };
		}
		if (v < 75) {
			return { label: "High", className: "evs-detail-drawer__attack-vector-severity--high" };
		}
		return { label: "Critical", className: "evs-detail-drawer__attack-vector-severity--critical" };
	};

	render() {
			const { show, evs, fullname, avatarInitial } = this.props;
		const target = evs?.target || {};
		const email = orDash(target?.email);
		const designation = orDash(target?.designation?.name);
		const department = orDash(target?.department?.name);
		const location = orDash(target?.location?.name);

		const attackTypeOptions = (this.state.attackTypes || []).map((attack) => ({
			label: attack.name,
			value: attack.id,
		}));
		const attackTypeIds = (this.state.selectedAttackOptions || []).map((opt) => opt.value);
		const gaugeValue = this.state.averageEvsScore;
		const severityInfo = this.getSeverityInfo(gaugeValue);

		return (
				<Modal
					className="info-filter right mainfooterModal right-modal-wrap evs-detail-drawer"
					show={show}
					onHide={this.handleClose}
				autoFocus
				keyboard
			>
				<Modal.Header closeButton>
					<div className="evs-detail-drawer__header">
						<div className="evs-detail-drawer__header-avatar">
							<div className="evs-detail-drawer__avatar">
								<span>{avatarInitial}</span>
							</div>
						</div>
						<div className="evs-detail-drawer__header-info">
							<h5 className="evs-detail-drawer__name">{fullname}</h5>
							<div className="evs-detail-drawer__header-details">
								<span className="evs-detail-drawer__header-detail">{email}</span>
								<span className="evs-detail-drawer__header-sep">|</span>
								<span className="evs-detail-drawer__header-detail">
									<PiBriefcase className="evs-detail-drawer__header-icon" />
									{designation}
								</span>
								<span className="evs-detail-drawer__header-sep">|</span>
								<span className="evs-detail-drawer__header-detail">
									<PiBuildings className="evs-detail-drawer__header-icon" />
									{department}
								</span>
								<span className="evs-detail-drawer__header-sep">|</span>
								<span className="evs-detail-drawer__header-detail">
									<PiMapPin className="evs-detail-drawer__header-icon" />
									{location}
								</span>
							</div>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body className="modalBody">
					<div>
						<div className="evs-detail-drawer__attack-vector">
							<div className="evs-detail-drawer__attack-vector-left">
								<h6 className="evs-detail-drawer__attack-vector-title">Attack Vector</h6>
								<MultiSelect
									className="evs-detail-drawer__attack-vector-multiselect"
									options={attackTypeOptions}
									value={this.state.selectedAttackOptions}
									onChange={(selected) => this.setState({ selectedAttackOptions: selected || [] })}
									// When nothing selected, treat as \"Overall\" (no attackTypeIds sent)
									overrideStrings={{ selectSomeItems: "Overall" }}
								/>
								<div className="evs-detail-drawer__attack-vector-meta">
									<span className="evs-detail-drawer__attack-vector-meta-label">Severity :</span>
									<span className={`evs-detail-drawer__attack-vector-severity ${severityInfo.className}`}>
										{severityInfo.label}
									</span>
								</div>
								{/* <div className="evs-detail-drawer__attack-vector-meta">
									<span className="evs-detail-drawer__attack-vector-meta-label">Last Updated : 14 Jan 2025 At 10:45 PM</span>
								</div> */}
							</div>
							<div className="evs-detail-drawer__attack-vector-chart">
								<EvsAttackVectorGauge value={gaugeValue} />
							</div>
						</div>
						<div className="evs-detail-drawer__tabs">
							<EvsDetailTabs
								activeTab={this.state.activeTab}
								onTabChange={this.handleTabChange}
								onExport={this.handleExport}
							/>
						</div>
						<div className="evs-detail-drawer__tab-content">
							{this.state.activeTab === "score-analysis" && (
								<EvsDetailScoreAnalysis
									evs={evs}
									attackTypeIds={attackTypeIds}
									onAverageEvsChange={this.handleAverageEvsChange}
								/>
							)}
							{this.state.activeTab === "campaigns" && (
								<EvsDetailCampaigns
									evs={evs}
									attackTypeIds={attackTypeIds}
									onAverageEvsChange={this.handleAverageEvsChange}
								/>
							)}
						</div>
					</div>
				</Modal.Body>
			</Modal>
		);
	}
}
